/* eslint-disable react-hooks/exhaustive-deps */
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    updateDoc,
    where,
} from "firebase/firestore";
import { auth, db } from "../../../firebase";
import { useLocationState } from "../../Utils";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Matiere } from "../matiere/matiere.type";
import { CompetenceStudent , CompetenceStudentGroup } from "./competenceStudent.type";
import { Competence } from "../competence/competence.type";
import { User } from "../user/user.type";



export const CompetenceStudentApi = createApi({
    reducerPath: "competenceStudentApi",
    tagTypes: ["competenceStudent", "competencesStudents","competencesByStudent", "studentByCompetenceId","competencesByUser",'allCompetencesStudents'],
    baseQuery: fakeBaseQuery(),
    endpoints: (builder) => ({
        addOrEditCompetenceStudent: builder.mutation({
            async queryFn({ id, ...data }) {

                try {
                    const user = auth.currentUser;
                    if (!user) {
                        return { error: { message: "Aucun utilisateur connecté." } };
                    }
                    const userId = user?.uid;
                    if (!id) {
                        
                        const docRef = await addDoc(collection(db, "competencesStudents"), {
                            ...data,
                            createdAt: serverTimestamp(),
                            createdBy: userId,
                            updatedAt: serverTimestamp(),
                        });
                        
                        return {
                            data: {
                                id: docRef.id,
                                message: "Compétence créée !",
                                ...data,
                            },
                        };
                        

                    } else {
                        // Mettre à jour Firestore
                        const compRef = doc(db, "competencesStudents", id);
                        await updateDoc(compRef, { ...data, updatedAt: serverTimestamp() });

                        return {
                            data: {
                                message:
                                    "Compétence modifiée !",
                            },
                        };
                    }
                } catch (error: any) {
                    console.log("ERROR API", error);
                    return { error: { status: "CUSTOM", message: error.message } };
                }
            },
            invalidatesTags: ["competenceStudent", "competencesStudents", 'competencesByStudent'],
        }),
        getCompetencesStudents: builder.query({
            
            async queryFn() {
                
                try {
                const user = auth.currentUser;
            
                if (!user) return { error: { message: "Aucun utilisateur connecté." } };

                const q = query(
                    collection(db, "competencesStudents"),
                    where("createdBy", "==", user.uid),
                    orderBy("createdAt", "desc")
                );


                const snapshot = await getDocs(q); 


                const competences = snapshot.docs.map((doc) => ({
                    uid: doc.id,
                    ...doc.data(),
                })) as any[];

                const competenceDatas = await Promise.all(
                    competences.map(async (competence) => {
                    if (!competence.competenceId) return competence;
                    try {
                        const dataRef = doc(db, "competences", competence.competenceId);
                        const dataSnap = await getDoc(dataRef);
                        return {
                        ...competence,
                        competenceData: dataSnap.exists()
                            ? { uid: dataSnap.id, ...dataSnap.data() }
                            : competence.competenceId,
                        };
                    } catch {
                        return competence;
                    }
                    })
                );


                const competencesWithMatiere = await Promise.all(
                    competenceDatas.map(async (competence) => {
                    if (!competence.matiereId) return competence;
                    try {
                        const matiereRef = doc(db, "matieres", competence.matiereId);
                        const matiereSnap = await getDoc(matiereRef);
                        return {
                        ...competence,
                        matiereData: matiereSnap.exists()
                            ? { uid: matiereSnap.id, ...matiereSnap.data() }
                            : competence.matiereId,
                        };
                    } catch {
                        return competence;
                    }
                    })
                );

                const grouped = groupByMatiere(competencesWithMatiere);

                return { data: grouped };
                } catch (error) {
                    console.error("ERREUR queryFn:", error);
                    return { error };
                }
            },

            keepUnusedDataFor: 60, 

            async onCacheEntryAdded(
                arg,
                { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
            ) {
                let unsubscribe: (() => void) | undefined;

                try {
                await cacheDataLoaded; 

                const user = auth.currentUser;
                if (!user) return;

                const q = query(
                    collection(db, "competencesStudents"),
                    where("createdBy", "==", user.uid),
                    orderBy("createdAt", "desc")
                );

                unsubscribe = onSnapshot(q, async (snapshot) => {
                    const competences = snapshot.docs.map((d) => ({
                    uid: d.id,
                    ...d.data(),
                    })) as any[];

                    const competenceDatas = await Promise.all(
                        competences.map(async (competence) => {
                            if (!competence.competenceId) return competence;
                            try {
                                const dataRef = doc(db, "competences", competence.competenceId);
                                const dataSnap = await getDoc(dataRef);
                                return {
                                ...competence,
                                competenceData: dataSnap.exists()
                                    ? { uid: dataSnap.id, ...dataSnap.data() }
                                    : competence.competenceId,
                                };
                            } catch {
                                return competence;
                            }
                        })
                    );

                    const competencesWithMatiere = await Promise.all(
                        competenceDatas.map(async (competence) => {
                            if (!competence.matiereId) return competence;
                            try {
                            const matiereRef = doc(db, "matieres", competence.matiereId);
                            const matiereSnap = await getDoc(matiereRef);
                            return {
                                ...competence,
                                matiereData: matiereSnap.exists()
                                ? { uid: matiereSnap.id, ...matiereSnap.data() }
                                : competence.matiereId,
                            };
                            } catch {
                            return competence;
                            }
                        })
                    );
                    const grouped = groupByMatiere(competencesWithMatiere);

                    updateCachedData(() => grouped);
                });

                } catch {
                // cacheDataLoaded rejeté → silencieux
                }

                await cacheEntryRemoved;
                if (unsubscribe) unsubscribe(); // nettoyage du listener
            },

            providesTags: ["competencesStudents"],
        }),
        getAllCompetencesStudents: builder.query({
            
            async queryFn() {
                
                try {
                const user = auth.currentUser;
            
                if (!user) return { error: { message: "Aucun utilisateur connecté." } };

                const q = query(
                    collection(db, "competencesStudents"),
                    where("createdBy", "==", user.uid),
                    orderBy("createdAt", "desc")
                );


                const snapshot = await getDocs(q); 


                const competences = snapshot.docs.map((doc) => ({
                    uid: doc.id,
                    ...doc.data(),
                })) as any[];

                const competenceDatas = await Promise.all(
                    competences.map(async (competence) => {
                    if (!competence.competenceId) return competence;
                    try {
                        const dataRef = doc(db, "competences", competence.competenceId);
                        const dataSnap = await getDoc(dataRef);
                        return {
                        ...competence,
                        competenceData: dataSnap.exists()
                            ? { uid: dataSnap.id, ...dataSnap.data() }
                            : competence.competenceId,
                        };
                    } catch {
                        return competence;
                    }
                    })
                );


                const competencesWithMatiere = await Promise.all(
                    competenceDatas.map(async (competence) => {
                    if (!competence.matiereId) return competence;
                    try {
                        const matiereRef = doc(db, "matieres", competence.matiereId);
                        const matiereSnap = await getDoc(matiereRef);
                        return {
                        ...competence,
                        matiereData: matiereSnap.exists()
                            ? { uid: matiereSnap.id, ...matiereSnap.data() }
                            : competence.matiereId,
                        };
                    } catch {
                        return competence;
                    }
                    })
                );

                return { data: competencesWithMatiere };
                } catch (error) {
                    console.error("ERREUR queryFn:", error);
                    return { error };
                }
            },

            keepUnusedDataFor: 60, 

            async onCacheEntryAdded(
                arg,
                { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
            ) {
                let unsubscribe: (() => void) | undefined;

                try {
                await cacheDataLoaded; 

                const user = auth.currentUser;
                if (!user) return;

                const q = query(
                    collection(db, "competencesStudents"),
                    where("createdBy", "==", user.uid),
                    orderBy("createdAt", "desc")
                );

                unsubscribe = onSnapshot(q, async (snapshot) => {
                    const competences = snapshot.docs.map((d) => ({
                    uid: d.id,
                    ...d.data(),
                    })) as any[];

                    const competenceDatas = await Promise.all(
                        competences.map(async (competence) => {
                            if (!competence.competenceId) return competence;
                            try {
                                const dataRef = doc(db, "competences", competence.competenceId);
                                const dataSnap = await getDoc(dataRef);
                                return {
                                ...competence,
                                competenceData: dataSnap.exists()
                                    ? { uid: dataSnap.id, ...dataSnap.data() }
                                    : competence.competenceId,
                                };
                            } catch {
                                return competence;
                            }
                        })
                    );

                    const competencesWithMatiere = await Promise.all(
                        competenceDatas.map(async (competence) => {
                            if (!competence.matiereId) return competence;
                            try {
                            const matiereRef = doc(db, "matieres", competence.matiereId);
                            const matiereSnap = await getDoc(matiereRef);
                            return {
                                ...competence,
                                matiereData: matiereSnap.exists()
                                ? { uid: matiereSnap.id, ...matiereSnap.data() }
                                : competence.matiereId,
                            };
                            } catch {
                            return competence;
                            }
                        })
                    );

                    updateCachedData(() => competencesWithMatiere);
                });

                } catch {
                // cacheDataLoaded rejeté → silencieux
                }

                await cacheEntryRemoved;
                if (unsubscribe) unsubscribe(); // nettoyage du listener
            },

            providesTags: ["allCompetencesStudents"],
        }),
        getCompetencesByStudent: builder.query({
            
            async queryFn(studentId: string) {

                
                if (!studentId) return { error: { message: "studentId manquant" } };

                try {
                const user = auth.currentUser;
            
                if (!user) return { error: { message: "Aucun utilisateur connecté." } };

                const q = query(
                    collection(db, "competencesStudents"),
                    where("createdBy", "==", user.uid),
                    where("studentId", "==", studentId),
                    orderBy("createdAt", "desc")
                );


                const snapshot = await getDocs(q); 


                const competences = snapshot.docs.map((doc) => ({
                    uid: doc.id,
                    ...doc.data(),
                })) as any[];

                const competenceDatas = await Promise.all(
                    competences.map(async (competence) => {
                    if (!competence.competenceId) return competence;
                    try {
                        const dataRef = doc(db, "competences", competence.competenceId);
                        const dataSnap = await getDoc(dataRef);
                        return {
                        ...competence,
                        competenceData: dataSnap.exists()
                            ? { uid: dataSnap.id, ...dataSnap.data() }
                            : competence.competenceId,
                        };
                    } catch {
                        return competence;
                    }
                    })
                );


                const competencesWithMatiere = await Promise.all(
                    competenceDatas.map(async (competence) => {
                    if (!competence.matiereId) return competence;
                    try {
                        const matiereRef = doc(db, "matieres", competence.matiereId);
                        const matiereSnap = await getDoc(matiereRef);
                        return {
                        ...competence,
                        matiereData: matiereSnap.exists()
                            ? { uid: matiereSnap.id, ...matiereSnap.data() }
                            : competence.matiereId,
                        };
                    } catch {
                        return competence;
                    }
                    })
                );

                const grouped = groupByMatiere(competencesWithMatiere);

                return { data: grouped };
                } catch (error) {
                    console.error("ERREUR queryFn:", error);
                    return { error };
                }
            },

            keepUnusedDataFor: 60, 

            async onCacheEntryAdded(
                arg,
                { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
            ) {
                let unsubscribe: (() => void) | undefined;

                try {
                await cacheDataLoaded; 

                const user = auth.currentUser;
                if (!user) return;

                const q = query(
                    collection(db, "competencesStudents"),
                    where("createdBy", "==", user.uid),
                    where("studentId", "==", arg),
                    orderBy("createdAt", "desc")
                );

                unsubscribe = onSnapshot(q, async (snapshot) => {
                    const competences = snapshot.docs.map((d) => ({
                    uid: d.id,
                    ...d.data(),
                    })) as any[];

                    const competenceDatas = await Promise.all(
                        competences.map(async (competence) => {
                            if (!competence.competenceId) return competence;
                            try {
                                const dataRef = doc(db, "competences", competence.competenceId);
                                const dataSnap = await getDoc(dataRef);
                                return {
                                ...competence,
                                competenceData: dataSnap.exists()
                                    ? { uid: dataSnap.id, ...dataSnap.data() }
                                    : competence.competenceId,
                                };
                            } catch {
                                return competence;
                            }
                        })
                    );

                    const competencesWithMatiere = await Promise.all(
                        competenceDatas.map(async (competence) => {
                            if (!competence.matiereId) return competence;
                            try {
                            const matiereRef = doc(db, "matieres", competence.matiereId);
                            const matiereSnap = await getDoc(matiereRef);
                            return {
                                ...competence,
                                matiereData: matiereSnap.exists()
                                ? { uid: matiereSnap.id, ...matiereSnap.data() }
                                : competence.matiereId,
                            };
                            } catch {
                            return competence;
                            }
                        })
                    );
                    const grouped = groupByMatiere(competencesWithMatiere);

                    updateCachedData(() => grouped);
                });

                } catch {
                // cacheDataLoaded rejeté → silencieux
                }

                await cacheEntryRemoved;
                if (unsubscribe) unsubscribe(); // nettoyage du listener
            },

            providesTags: ["competencesByStudent"],
        }),

        getCompetencesByUser: builder.query({
            
            async queryFn(studentId: string) {

            
                try {
                // const user = auth.currentUser;
            
                // if (!user) return { error: { message: "Aucun utilisateur connecté." } };

                const q = query(
                    collection(db, "competencesStudents"),
                    where("studentId", "==", studentId),
                    orderBy("createdAt", "desc")
                );


                const snapshot = await getDocs(q); 

                const competences = snapshot.docs.map((doc) => ({
                    uid: doc.id,
                    ...doc.data(),
                })) as any[];

                const competenceDatas = await Promise.all(
                    competences.map(async (competence) => {
                    if (!competence.competenceId) return competence;
                    try {
                        const dataRef = doc(db, "competences", competence.competenceId);
                        const dataSnap = await getDoc(dataRef);
                        return {
                        ...competence,
                        competenceData: dataSnap.exists()
                            ? { uid: dataSnap.id, ...dataSnap.data() }
                            : competence.competenceId,
                        };
                    } catch {
                        return competence;
                    }
                    })
                );


                const competencesWithMatiere = await Promise.all(
                    competenceDatas.map(async (competence) => {
                    if (!competence.matiereId) return competence;
                    try {
                        const matiereRef = doc(db, "matieres", competence.matiereId);
                        const matiereSnap = await getDoc(matiereRef);
                        return {
                        ...competence,
                        matiereData: matiereSnap.exists()
                            ? { uid: matiereSnap.id, ...matiereSnap.data() }
                            : competence.matiereId,
                        };
                    } catch {
                        return competence;
                    }
                    })
                );

                const grouped = groupByMatiere(competencesWithMatiere);

                return { data: grouped };
                } catch (error) {
                    console.error("ERREUR queryFn:", error);
                    return { error };
                }
            },

            keepUnusedDataFor: 60, 

            async onCacheEntryAdded(
                arg,
                { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
            ) {
                let unsubscribe: (() => void) | undefined;

                try {
                await cacheDataLoaded; 

                const user = auth.currentUser;
                if (!user) return;

                const q = query(
                    collection(db, "competencesStudents"),
                    where("studentId", "==", arg),
                    orderBy("createdAt", "desc")
                );

                unsubscribe = onSnapshot(q, async (snapshot) => {
                    const competences = snapshot.docs.map((d) => ({
                    uid: d.id,
                    ...d.data(),
                    })) as any[];

                    const competenceDatas = await Promise.all(
                        competences.map(async (competence) => {
                            if (!competence.competenceId) return competence;
                            try {
                                const dataRef = doc(db, "competences", competence.competenceId);
                                const dataSnap = await getDoc(dataRef);
                                return {
                                ...competence,
                                competenceData: dataSnap.exists()
                                    ? { uid: dataSnap.id, ...dataSnap.data() }
                                    : competence.competenceId,
                                };
                            } catch {
                                return competence;
                            }
                        })
                    );

                    const competencesWithMatiere = await Promise.all(
                        competenceDatas.map(async (competence) => {
                            if (!competence.matiereId) return competence;
                            try {
                            const matiereRef = doc(db, "matieres", competence.matiereId);
                            const matiereSnap = await getDoc(matiereRef);
                            return {
                                ...competence,
                                matiereData: matiereSnap.exists()
                                ? { uid: matiereSnap.id, ...matiereSnap.data() }
                                : competence.matiereId,
                            };
                            } catch {
                            return competence;
                            }
                        })
                    );
                    const grouped = groupByMatiere(competencesWithMatiere);

                    updateCachedData(() => grouped);
                });

                } catch {
                // cacheDataLoaded rejeté → silencieux
                }

                await cacheEntryRemoved;
                if (unsubscribe) unsubscribe(); // nettoyage du listener
            },

            providesTags: ["competencesByUser"],
        }),
    
        getCompetenceStudentById: builder.query({
            queryFn: async (CompetenceId) => {
                try {
                    const CompetenceStudentRef = doc(db, "competencesStudents", CompetenceId);
                    const CompetenceSnap = await getDoc(CompetenceStudentRef);

                    if (!CompetenceSnap.exists()) {
                        return {
                            error: { status: 404, message: "Compétence introuvable !" },
                        };
                    }

                    return { data: { id: CompetenceSnap.id, ...CompetenceSnap.data() } };
                } catch (error: any) {
                    return { error: { status: 500, message: error.message } };
                }
            },
            providesTags: ["competenceStudent"],
        }),
        getStudentsByCompetenceId: builder.query({
            queryFn: async ({ competenceId, matiereId }: { competenceId: string | number; matiereId?: string }) => {
                try {
                    const user = auth.currentUser;
                    if (!user) return { error: { message: "Aucun utilisateur connecté." } };
                    // 1. Récupérer tous les competencesStudents liés au competenceId
                    const competencesStudentsRef = collection(db, "competencesStudents");
                    const constraints = [
                        where("competenceId", "==", competenceId),
                        where("createdBy", "==", user.uid),
                    ];

                    if (matiereId) {
                        constraints.push(where("matiereId", "==", matiereId));
                    }

                    const q = query(competencesStudentsRef, ...constraints);
                    const competencesStudentsSnap = await getDocs(q);

                    if (competencesStudentsSnap.empty) {
                        return { data: [] };
                    }

                     // 2. Pour chaque competenceStudent, récupérer le student et sa classe
                    const students = await Promise.all(
                        competencesStudentsSnap.docs.map(async (competenceStudentDoc) => {
                            const competenceStudentData = competenceStudentDoc.data();

                            // Récupérer le student
                            const studentRef = doc(db, "users", competenceStudentData.studentId);
                            const studentSnap = await getDoc(studentRef);

                            if (!studentSnap.exists()) return null;

                            const studentData = { id: studentSnap.id, ...studentSnap.data() } as User & { id: string };

                            // Récupérer la classe du student
                            let classeData = null;
                            if (studentData?.classe) {
                                const classeRef = doc(db, "classrooms", studentData.classe);
                                const classeSnap = await getDoc(classeRef);
                                classeData = classeSnap.exists()
                                    ? { id: classeSnap.id, ...classeSnap.data() }
                                    : null;
                            }

                            return { ...studentData, classeData } as any ;                        })
                    );

                    const uniqueStudents = Array.from(
                        new Map(
                            students
                                .filter((student: any | null) => student !== null)
                                .map((student: any) => [student.id, student])
                        ).values()
                    );

                    return { data: uniqueStudents };
                } catch (error: any) {
                    return { error: { status: 500, message: error.message } };
                }
            },
            providesTags: ["studentByCompetenceId"],
        }),
        // Supprimer
        deleteCompetenceStudent: builder.mutation({
            queryFn: async (uid) => {
                try {
                    const docRef = doc(db, "competencesStudents", uid);
                
                
                    // 1. Récupérer le document avant suppression
                    const docSnap = await getDoc(docRef);
                    const competenceStudent = docSnap.data() as CompetenceStudent;
                    const { competenceId , studentId } = competenceStudent;

                    // 2. Supprimer le competenceStudent
                    await deleteDoc(docRef);

                    // 3. Mettre à jour le competence lié
                    if (competenceId && typeof competenceId == "string") {
                        const competenceRef = doc(db, "competences", competenceId);
                        const competenceSnap = await getDoc(competenceRef);
                        const competenceData = competenceSnap.data() as Competence;

                        // Retirer le student de la liste
                        const updatedStudents = competenceData.students.filter(
                            (id) => id !== studentId
                        );

                        await updateDoc(competenceRef, {
                            students: updatedStudents,
                            updatedAt: serverTimestamp(),
                        });
                    }

                    return {
                        data: { uid, message: "Compétence supprimée !", success: true },
                    };
                } catch (error: any) {
                    return { error: { status: "DELETE_ERROR", message: error.message } };
                }
            },
            invalidatesTags: ["competenceStudent", "competencesStudents", 'competencesByStudent'],
        }),
    }),
});

export const {
    useAddOrEditCompetenceStudentMutation,
    useGetCompetencesStudentsQuery,
    useDeleteCompetenceStudentMutation,
    useLazyGetCompetenceStudentByIdQuery,
    useGetCompetencesByStudentQuery,
    useLazyGetStudentsByCompetenceIdQuery,
    useGetCompetencesByUserQuery,
    useGetAllCompetencesStudentsQuery,
} = CompetenceStudentApi;

export function useCompetenceStudentFromLocation(): [
    CompetenceStudent,
    boolean,
    string,
    (id: string) => any
] {
    const itemState = useLocationState<CompetenceStudent | undefined>(undefined);
    const [item, setItem] = useState(itemState);
    const { id } = useParams<{ id: string }>();
    const [findById, { data, isError, isLoading }] = useLazyGetCompetenceStudentByIdQuery();
    const navigate = useNavigate();

    useEffect(() => {
        if (id) {
            findById(id as string);
        }
    }, [id]);
    useEffect(() => {
        if (isError && !itemState) {
            navigate(-1);
        }
    }, [isError]);
    useEffect(() => {
        if (data) {
            setItem(data as CompetenceStudent);
        }
    }, [data]);

    return [item as CompetenceStudent & { bio: string }, isLoading, id as string, findById];
}

const groupByMatiere = (competences: CompetenceStudent[]): CompetenceStudentGroup[] => {
    const map = new Map<string, CompetenceStudentGroup>();

    for (const competence of competences) {
        const matiereData =
            competence.matiereData && typeof competence.matiereData === "object"
                ? (competence.matiereData as Matiere)
                : null;

        const key = matiereData?.id ?? competence.matiereId ?? "sans-matiere";
        const title = matiereData?.title ?? competence.matiereId ?? "Sans matière";

        if (!map.has(key)) {
            map.set(key, {
                id: key,
                matiereTitle: title,
                matiereData: matiereData,
                competences: [],
            });
        }

        map.get(key)!.competences.push(competence);
    }

    // Tri alphabétique sur le titre de la matière
    return Array.from(map.values()).sort((a, b) =>
        a.matiereTitle.localeCompare(b.matiereTitle, "fr")
    );
}

